import { ChevronDown, ChevronRight, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { AddKeyControl } from './AddKeyControl';
import type { PathType } from '../types';

const DEFAULT_COLLAPSED_DEPTH = 2; //Tiefe ab der ein Container automatisch zugeklappt rendert

type Primitive = string | number | boolean | null;

function isPrimitive(value: unknown): value is Primitive {
  return (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  );
}

/**
 * Diese Funktion sorgt dafür, das string tatsächlich mit Anführungszeiche angezeigt werden,
 * dies sorgt dafür, das der User den Datentypen besser unterscheiden kann.
 */
function previewValue(value: Primitive): string {
  if (value === null) return 'null';
  return typeof value === 'string' ? `"${value}"` : String(value);
}

function joinPath(prefix: string, key: string | number): string {
  return prefix === '' ? String(key) : `${prefix}.${key}`;
}

interface PayloadTreeProps {
  value: unknown;
  selectedPaths: ReadonlySet<string>;
  pathsToDelete: ReadonlySet<string>;
  onTogglePath: (path: string) => void;
  onAddPath: (path: string, type: PathType) => void;
  onToggleDelete: (path: string) => void;
  pathPrefix?: string;
  depth?: number;
  // True wenn ein Vorfahre dieses Teilbaums zum Löschen markiert ist —
  // dann sind alle Aktionen auf Kindern blockiert.
  parentBlocked?: boolean;
}

export const PayloadTree: React.FC<PayloadTreeProps> = ({
  value,
  selectedPaths,
  pathsToDelete,
  onTogglePath,
  onAddPath,
  onToggleDelete,
  pathPrefix = '',
  depth = 0,
  parentBlocked = false,
}) => {
  if (Array.isArray(value)) {
    return (
      <ul className="space-y-1">
        {value.map((item, idx) => (
          <TreeNode
            key={idx}
            label={`[${idx}]`}
            path={joinPath(pathPrefix, idx)}
            value={item}
            selectedPaths={selectedPaths}
            pathsToDelete={pathsToDelete}
            onTogglePath={onTogglePath}
            onAddPath={onAddPath}
            onToggleDelete={onToggleDelete}
            depth={depth}
            parentBlocked={parentBlocked}
          />
        ))}
      </ul>
    );
  }

  if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    return (
      <ul className="space-y-1">
        {entries.map(([key, child]) => (
          <TreeNode
            key={key}
            label={key}
            path={joinPath(pathPrefix, key)}
            value={child}
            selectedPaths={selectedPaths}
            pathsToDelete={pathsToDelete}
            onTogglePath={onTogglePath}
            onAddPath={onAddPath}
            onToggleDelete={onToggleDelete}
            depth={depth}
            parentBlocked={parentBlocked}
          />
        ))}
        <li>
          <AddKeyControl
            parentPath={pathPrefix}
            onAddPath={onAddPath}
            disabled={parentBlocked}
          />
        </li>
      </ul>
    );
  }

  return null;
};

interface TreeNodeProps {
  label: string;
  path: string;
  value: unknown;
  selectedPaths: ReadonlySet<string>;
  pathsToDelete: ReadonlySet<string>;
  onTogglePath: (path: string) => void;
  onAddPath: (path: string, type: PathType) => void;
  onToggleDelete: (path: string) => void;
  depth: number;
  parentBlocked: boolean;
}

const TreeNode: React.FC<TreeNodeProps> = ({
  label,
  path,
  value,
  selectedPaths,
  pathsToDelete,
  onTogglePath,
  onAddPath,
  onToggleDelete,
  depth,
  parentBlocked,
}) => {
  const [open, setOpen] = useState(depth < DEFAULT_COLLAPSED_DEPTH);
  const isSelected = selectedPaths.has(path);
  const isMarkedDelete = pathsToDelete.has(path);
  const isAncestorBlocked = parentBlocked;
  const childrenBlocked = parentBlocked || isMarkedDelete;

  if (isPrimitive(value)) {
    return (
      <li className="group flex items-center gap-1">
        <label
          className={cn(
            'flex flex-1 items-baseline gap-2 rounded px-1 py-0.5 text-sm',
            isAncestorBlocked
              ? 'cursor-not-allowed opacity-50'
              : 'cursor-pointer hover:bg-accent',
            isMarkedDelete && 'line-through opacity-60',
          )}
        >
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => onTogglePath(path)}
            disabled={isMarkedDelete || isAncestorBlocked}
          />
          <span className="font-mono text-text-primary">{label}</span>
          <span
            className={cn(
              'truncate font-mono text-xs',
              value === null
                ? 'text-text-secondary italic'
                : 'text-text-secondary',
            )}
            title={previewValue(value)}
          >
            {previewValue(value)}
          </span>
        </label>
        <DeleteButton
          isMarkedDelete={isMarkedDelete}
          disabled={isAncestorBlocked}
          onClick={() => onToggleDelete(path)}
          path={path}
        />
      </li>
    );
  }

  return (
    <li>
      <div
        className={cn(
          'group flex items-center gap-1',
          isAncestorBlocked && 'opacity-50',
          isMarkedDelete && 'line-through opacity-60',
        )}
      >
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className="flex flex-1 items-center gap-1 rounded px-1 py-0.5 text-left text-sm hover:bg-accent"
        >
          {open ? (
            <ChevronDown className="size-3.5 text-text-secondary" />
          ) : (
            <ChevronRight className="size-3.5 text-text-secondary" />
          )}
          <span className="font-mono text-text-primary">{label}</span>
          <span className="font-mono text-xs text-text-secondary">
            {Array.isArray(value)
              ? `[${value.length}]`
              : `{${Object.keys(value as Record<string, unknown>).length}}`}
          </span>
        </button>
        <DeleteButton
          isMarkedDelete={isMarkedDelete}
          disabled={isAncestorBlocked}
          onClick={() => onToggleDelete(path)}
          path={path}
        />
      </div>
      {open ? (
        <div className="ml-4 border-l pl-2">
          <PayloadTree
            value={value}
            selectedPaths={selectedPaths}
            pathsToDelete={pathsToDelete}
            onTogglePath={onTogglePath}
            onAddPath={onAddPath}
            onToggleDelete={onToggleDelete}
            pathPrefix={path}
            depth={depth + 1}
            parentBlocked={childrenBlocked}
          />
        </div>
      ) : null}
    </li>
  );
};

interface DeleteButtonProps {
  isMarkedDelete: boolean;
  disabled: boolean;
  path: string;
  onClick: () => void;
}

const DeleteButton: React.FC<DeleteButtonProps> = ({
  isMarkedDelete,
  disabled,
  path,
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={cn(
      'rounded p-0.5 text-text-secondary opacity-0 hover:bg-accent hover:text-red-600 group-hover:opacity-100 focus:opacity-100',
      isMarkedDelete && 'text-red-600 opacity-100',
      disabled &&
        'cursor-not-allowed opacity-50 hover:bg-transparent hover:text-text-secondary',
    )}
    aria-label={
      disabled
        ? `Übergeordneter Pfad von ${path} ist zum Löschen markiert`
        : isMarkedDelete
          ? `Löschen von ${path} rückgängig`
          : `Pfad ${path} zum Löschen markieren`
    }
    title={
      disabled
        ? 'Übergeordneter Pfad ist zum Löschen markiert'
        : isMarkedDelete
          ? 'Löschen rückgängig'
          : 'Beim Speichern aus JSON entfernen'
    }
  >
    <Trash2 className="size-3.5" />
  </button>
);
